<template>
  <section class="px-6 py-6 space-y-6 min-h-screen font-light select-none text-left transition-colors duration-200"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-gray-50 text-gray-700'">

    <!-- Header -->
    <div class="flex justify-between items-center border-b pb-5 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900/60' : 'border-gray-200'">
      <div class="space-y-1">
        <h1 class="text-xl font-medium tracking-tight transition-colors duration-200"
          :class="isDark ? 'text-white' : 'text-gray-900'">Promotions & Fidélité</h1>
        <p class="text-xs font-light transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Créez des promotions et gérez vos programmes de fidélité client.</p>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex border-b transition-colors duration-200"
      :class="isDark ? 'border-zinc-900/60' : 'border-gray-200'">
      <button
        @click="activeTab = 'promotions'"
        class="px-6 py-3 text-sm font-medium border-b-2 transition-all duration-200"
        :class="activeTab === 'promotions'
          ? isDark
            ? 'border-white text-white'
            : 'border-amber-600 text-amber-600'
          : isDark
            ? 'border-transparent text-zinc-500 hover:text-zinc-300'
            : 'border-transparent text-gray-500 hover:text-gray-700'"
      >
        <i class="fas fa-tag mr-2"></i>
        Promotions
      </button>
      <button
        @click="activeTab = 'fidelite'"
        class="px-6 py-3 text-sm font-medium border-b-2 transition-all duration-200"
        :class="activeTab === 'fidelite'
          ? isDark
            ? 'border-white text-white'
            : 'border-amber-600 text-amber-600'
          : isDark
            ? 'border-transparent text-zinc-500 hover:text-zinc-300'
            : 'border-transparent text-gray-500 hover:text-gray-700'"
      >
        <i class="fas fa-award mr-2"></i>
        Fidélité
      </button>
    </div>

    <!-- Contenu Promotions -->
    <div v-if="activeTab === 'promotions'" class="space-y-6">
      <div class="flex justify-end">
        <button
          @click="openPromotionModal()"
          class="px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 shadow-lg active:scale-[0.99]"
          :class="isDark
            ? 'bg-white hover:bg-[#D5CCBC] text-black shadow-white/5'
            : 'bg-amber-600 hover:bg-amber-700 text-white shadow-amber-600/20'"
        >
          <i class="fas fa-plus mr-2"></i>
          Nouvelle Promotion
        </button>
      </div>

      <!-- Liste des promotions -->
      <div v-if="promotions.length === 0" class="border rounded-2xl p-12 text-center transition-colors duration-200"
        :class="isDark
          ? 'bg-zinc-900/20 border-zinc-900'
          : 'bg-white border-gray-200'">
        <i class="fas fa-tags text-5xl mb-4 transition-colors duration-200"
          :class="isDark ? 'text-zinc-700' : 'text-gray-300'"></i>
        <p class="text-sm transition-colors duration-200"
          :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Aucune promotion pour le moment</p>
        <p class="text-xs mt-1 transition-colors duration-200"
          :class="isDark ? 'text-zinc-600' : 'text-gray-500'">Créez votre première promotion pour attirer plus de clients</p>
      </div>

      <div v-else class="grid md:grid-cols-2 gap-4">
        <div
          v-for="promo in promotions"
          :key="promo._id"
          class="border rounded-xl p-5 transition-all duration-200 hover:shadow-lg"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900 hover:border-zinc-800'
            : 'bg-white border-gray-200 hover:border-gray-300'"
        >
          <!-- Header de la carte -->
          <div class="flex items-start justify-between mb-4">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <h3 class="text-sm font-semibold transition-colors duration-200"
                  :class="isDark ? 'text-white' : 'text-gray-900'">{{ promo.nom }}</h3>
                <span
                  class="px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider border"
                  :class="promo.actif
                    ? isDark
                      ? 'bg-emerald-950/40 text-emerald-400 border-emerald-900/40'
                      : 'bg-emerald-100 text-emerald-700 border-emerald-300'
                    : isDark
                      ? 'bg-zinc-900 text-zinc-600 border-zinc-800'
                      : 'bg-gray-100 text-gray-500 border-gray-300'"
                >
                  {{ promo.actif ? 'Active' : 'Inactive' }}
                </span>
              </div>
              <p v-if="promo.description" class="text-xs transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">{{ promo.description }}</p>
            </div>
          </div>

          <!-- Détails de la promotion -->
          <div class="space-y-3">
            <!-- Type et valeur -->
            <div class="flex items-center gap-2">
              <div class="px-3 py-2 rounded-lg border transition-colors duration-200 flex-1"
                :class="isDark
                  ? 'bg-purple-950/20 border-purple-900/40'
                  : 'bg-purple-50 border-purple-200'">
                <p class="text-xs uppercase tracking-wider font-medium transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Réduction</p>
                <p class="text-lg font-bold transition-colors duration-200"
                  :class="isDark ? 'text-purple-400' : 'text-purple-700'">
                  {{ formatPromoValue(promo) }}
                </p>
              </div>
              <div v-if="promo.code" class="px-3 py-2 rounded-lg border transition-colors duration-200 flex-1"
                :class="isDark
                  ? 'bg-amber-950/20 border-amber-900/40'
                  : 'bg-amber-50 border-amber-200'">
                <p class="text-xs uppercase tracking-wider font-medium transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Code</p>
                <p class="text-sm font-mono font-bold transition-colors duration-200"
                  :class="isDark ? 'text-amber-400' : 'text-amber-700'">
                  {{ promo.code }}
                </p>
              </div>
            </div>

            <!-- Période -->
            <div class="flex items-center gap-2 text-xs transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
              <i class="fas fa-calendar"></i>
              <span>{{ formatDate(promo.dateDebut) }} - {{ formatDate(promo.dateFin) }}</span>
            </div>

            <!-- Conditions -->
            <div v-if="promo.conditions.montantMinimum > 0" class="flex items-center gap-2 text-xs transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
              <i class="fas fa-euro-sign"></i>
              <span>Montant minimum : {{ promo.conditions.montantMinimum }}€</span>
            </div>

            <!-- Utilisation -->
            <div v-if="promo.limiteUtilisationTotale" class="flex items-center gap-2 text-xs transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
              <i class="fas fa-chart-bar"></i>
              <span>{{ promo.utilisations }}/{{ promo.limiteUtilisationTotale }} utilisations</span>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-2 mt-4 pt-4 border-t transition-colors duration-200"
            :class="isDark ? 'border-zinc-800' : 'border-gray-200'">
            <button
              @click="togglePromotion(promo._id)"
              class="flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors"
              :class="isDark
                ? 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'"
            >
              <i :class="promo.actif ? 'fas fa-pause' : 'fas fa-play'" class="mr-2"></i>
              {{ promo.actif ? 'Désactiver' : 'Activer' }}
            </button>
            <button
              @click="openPromotionModal(promo)"
              class="px-3 py-2 rounded-lg text-xs font-medium transition-colors"
              :class="isDark
                ? 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'"
            >
              <i class="fas fa-edit"></i>
            </button>
            <button
              @click="deletePromotion(promo._id)"
              class="px-3 py-2 rounded-lg text-xs font-medium transition-colors text-red-400 hover:bg-red-950/20 border"
              :class="isDark ? 'border-red-900/40' : 'border-red-200'"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Contenu Fidélité -->
    <div v-if="activeTab === 'fidelite'" class="space-y-6">
      <div class="flex justify-end">
        <button
          @click="openFideliteModal()"
          class="px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 shadow-lg active:scale-[0.99]"
          :class="isDark
            ? 'bg-white hover:bg-[#D5CCBC] text-black shadow-white/5'
            : 'bg-amber-600 hover:bg-amber-700 text-white shadow-amber-600/20'"
        >
          <i class="fas fa-plus mr-2"></i>
          Nouveau Programme
        </button>
      </div>

      <!-- Liste des programmes de fidélité -->
      <div v-if="programmesFidelite.length === 0" class="border rounded-2xl p-12 text-center transition-colors duration-200"
        :class="isDark
          ? 'bg-zinc-900/20 border-zinc-900'
          : 'bg-white border-gray-200'">
        <i class="fas fa-award text-5xl mb-4 transition-colors duration-200"
          :class="isDark ? 'text-zinc-700' : 'text-gray-300'"></i>
        <p class="text-sm transition-colors duration-200"
          :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Aucun programme de fidélité</p>
        <p class="text-xs mt-1 transition-colors duration-200"
          :class="isDark ? 'text-zinc-600' : 'text-gray-500'">Créez un programme pour fidéliser vos clients</p>
      </div>

      <div v-else class="grid md:grid-cols-2 gap-4">
        <div
          v-for="(programme, index) in programmesFidelite"
          :key="index"
          class="border rounded-xl p-5 transition-all duration-200 hover:shadow-lg"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900 hover:border-zinc-800'
            : 'bg-white border-gray-200 hover:border-gray-300'"
        >
          <!-- Header -->
          <div class="flex items-start justify-between mb-4">
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-1">
                <h3 class="text-sm font-semibold transition-colors duration-200"
                  :class="isDark ? 'text-white' : 'text-gray-900'">{{ programme.nom }}</h3>
                <span
                  class="px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider border"
                  :class="programme.actif
                    ? isDark
                      ? 'bg-emerald-950/40 text-emerald-400 border-emerald-900/40'
                      : 'bg-emerald-100 text-emerald-700 border-emerald-300'
                    : isDark
                      ? 'bg-zinc-900 text-zinc-600 border-zinc-800'
                      : 'bg-gray-100 text-gray-500 border-gray-300'"
                >
                  {{ programme.actif ? 'Actif' : 'Inactif' }}
                </span>
              </div>
              <span class="inline-block px-2 py-0.5 rounded text-[10px] font-medium transition-colors duration-200"
                :class="isDark
                  ? 'bg-blue-950/40 text-blue-400'
                  : 'bg-blue-100 text-blue-700'">
                {{ programme.type }}
              </span>
            </div>
          </div>

          <!-- Détails du programme -->
          <div class="space-y-3">
            <!-- Mécanisme -->
            <div class="p-3 rounded-lg border transition-colors duration-200"
              :class="isDark
                ? 'bg-purple-950/20 border-purple-900/40'
                : 'bg-purple-50 border-purple-200'">
              <p class="text-xs uppercase tracking-wider font-medium mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Mécanisme</p>
              <p class="text-xs transition-colors duration-200"
                :class="isDark ? 'text-purple-400' : 'text-purple-700'">
                <span v-if="programme.type === 'Points'">
                  {{ programme.euroParPoint }}€ = 1 point<br/>
                  {{ programme.pointsPourReduction }} points = réduction
                </span>
                <span v-else>
                  {{ programme.rdvPourReduction }} rendez-vous = réduction
                </span>
              </p>
            </div>

            <!-- Récompense -->
            <div class="p-3 rounded-lg border transition-colors duration-200"
              :class="isDark
                ? 'bg-amber-950/20 border-amber-900/40'
                : 'bg-amber-50 border-amber-200'">
              <p class="text-xs uppercase tracking-wider font-medium mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Récompense</p>
              <p class="text-xs font-bold transition-colors duration-200"
                :class="isDark ? 'text-amber-400' : 'text-amber-700'">
                {{ programme.reductionPourcentage }}% de réduction
                <span class="font-normal">(max {{ programme.plafondReduction }}€)</span>
              </p>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-2 mt-4 pt-4 border-t transition-colors duration-200"
            :class="isDark ? 'border-zinc-800' : 'border-gray-200'">
            <button
              @click="toggleProgramme(index)"
              class="flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors"
              :class="isDark
                ? 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'"
            >
              <i :class="programme.actif ? 'fas fa-pause' : 'fas fa-play'" class="mr-2"></i>
              {{ programme.actif ? 'Désactiver' : 'Activer' }}
            </button>
            <button
              @click="openFideliteModal(programme, index)"
              class="px-3 py-2 rounded-lg text-xs font-medium transition-colors"
              :class="isDark
                ? 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'"
            >
              <i class="fas fa-edit"></i>
            </button>
            <button
              @click="deleteProgramme(index)"
              class="px-3 py-2 rounded-lg text-xs font-medium transition-colors text-red-400 hover:bg-red-950/20 border"
              :class="isDark ? 'border-red-900/40' : 'border-red-200'"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Promotion -->
    <Teleport to="body">
      <div
        v-if="showPromotionModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        @click.self="closePromotionModal"
      >
        <div
          class="border rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900 border-zinc-800'
            : 'bg-white border-gray-200'"
        >
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-lg font-semibold transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">
              {{ editingPromotion ? 'Modifier la promotion' : 'Nouvelle promotion' }}
            </h2>
            <button
              @click="closePromotionModal"
              class="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
              :class="isDark
                ? 'text-zinc-500 hover:bg-zinc-800'
                : 'text-gray-500 hover:bg-gray-100'"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <form @submit.prevent="savePromotion" class="space-y-4">
            <!-- Nom -->
            <div>
              <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Nom de la promotion *</label>
              <input
                v-model="promotionForm.nom"
                type="text"
                required
                class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                :class="isDark
                  ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                placeholder="Ex: Promotion Été 2026"
              />
            </div>

            <!-- Description -->
            <div>
              <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Description</label>
              <textarea
                v-model="promotionForm.description"
                rows="3"
                class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all resize-none"
                :class="isDark
                  ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                placeholder="Description de la promotion..."
              ></textarea>
            </div>

            <!-- Type et Valeur -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Type de réduction *</label>
                <select
                  v-model="promotionForm.type"
                  required
                  class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                  :class="isDark
                    ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700'
                    : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                >
                  <option value="percentage">Pourcentage</option>
                  <option value="fixed_amount">Montant fixe</option>
                  <option value="free_service">Service gratuit</option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Valeur *</label>
                <div class="relative">
                  <input
                    v-model.number="promotionForm.valeur"
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                    :class="isDark
                      ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700'
                      : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                    placeholder="Ex: 20"
                  />
                  <span class="absolute right-4 top-1/2 -translate-y-1/2 text-xs transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-500'">
                    {{ promotionForm.type === 'percentage' ? '%' : '€' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Code promo (optionnel) -->
            <div>
              <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Code promo (optionnel)</label>
              <input
                v-model="promotionForm.code"
                type="text"
                class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm uppercase transition-all"
                :class="isDark
                  ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                placeholder="Ex: SUMMER2026"
              />
            </div>

            <!-- Période de validité -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Date de début *</label>
                <input
                  v-model="promotionForm.dateDebut"
                  type="date"
                  required
                  class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                  :class="isDark
                    ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700'
                    : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                />
              </div>
              <div>
                <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Date de fin *</label>
                <input
                  v-model="promotionForm.dateFin"
                  type="date"
                  required
                  class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                  :class="isDark
                    ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700'
                    : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                />
              </div>
            </div>

            <!-- Conditions -->
            <div class="p-4 rounded-xl border transition-colors duration-200"
              :class="isDark ? 'border-zinc-800 bg-zinc-950/50' : 'border-gray-200 bg-gray-50'">
              <h3 class="text-sm font-medium mb-3 transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-gray-900'">Conditions (optionnel)</h3>

              <div class="space-y-3">
                <div>
                  <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                    :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Montant minimum d'achat (€)</label>
                  <input
                    v-model.number="promotionForm.conditions.montantMinimum"
                    type="number"
                    min="0"
                    step="0.01"
                    class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                    :class="isDark
                      ? 'border-zinc-800 bg-zinc-900 text-white focus:border-zinc-700'
                      : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                    :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Limite d'utilisation totale</label>
                  <input
                    v-model.number="promotionForm.limiteUtilisationTotale"
                    type="number"
                    min="1"
                    class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                    :class="isDark
                      ? 'border-zinc-800 bg-zinc-900 text-white focus:border-zinc-700'
                      : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                    placeholder="Illimité"
                  />
                </div>

                <label class="flex items-center gap-3 cursor-pointer">
                  <input
                    v-model="promotionForm.conditions.premiereVisiteUniquement"
                    type="checkbox"
                    class="h-4 w-4 rounded cursor-pointer transition-colors duration-200"
                    :class="isDark
                      ? 'border-zinc-800 bg-zinc-900 accent-zinc-200'
                      : 'border-gray-300 bg-white accent-amber-600'"
                  />
                  <span class="text-xs transition-colors duration-200"
                    :class="isDark ? 'text-zinc-400' : 'text-gray-600'">
                    Réservé aux nouveaux clients uniquement
                  </span>
                </label>
              </div>
            </div>

            <!-- Statut -->
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="promotionForm.actif"
                type="checkbox"
                class="h-4 w-4 rounded cursor-pointer transition-colors duration-200"
                :class="isDark
                  ? 'border-zinc-800 bg-zinc-900 accent-zinc-200'
                  : 'border-gray-300 bg-white accent-amber-600'"
              />
              <span class="text-xs transition-colors duration-200"
                :class="isDark ? 'text-zinc-400' : 'text-gray-600'">
                Activer immédiatement cette promotion
              </span>
            </label>

            <!-- Actions -->
            <div class="flex gap-3 pt-4">
              <button
                type="button"
                @click="closePromotionModal"
                class="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
                :class="isDark
                  ? 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'"
              >
                Annuler
              </button>
              <button
                type="submit"
                :disabled="isSaving"
                class="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
                :class="isDark
                  ? 'bg-white hover:bg-[#D5CCBC] text-black'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'"
              >
                {{ isSaving ? 'Enregistrement...' : (editingPromotion ? 'Modifier' : 'Créer') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Modal Fidélité -->
    <Teleport to="body">
      <div
        v-if="showFideliteModal"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        @click.self="closeFideliteModal"
      >
        <div
          class="border rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900 border-zinc-800'
            : 'bg-white border-gray-200'"
        >
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-lg font-semibold transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">
              {{ editingProgramme !== null ? 'Modifier le programme' : 'Nouveau programme de fidélité' }}
            </h2>
            <button
              @click="closeFideliteModal"
              class="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
              :class="isDark
                ? 'text-zinc-500 hover:bg-zinc-800'
                : 'text-gray-500 hover:bg-gray-100'"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <form @submit.prevent="saveProgramme" class="space-y-4">
            <!-- Nom -->
            <div>
              <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Nom du programme *</label>
              <input
                v-model="fideliteForm.nom"
                type="text"
                required
                class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                :class="isDark
                  ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                placeholder="Ex: Carte de Fidélité Or"
              />
            </div>

            <!-- Type -->
            <div>
              <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Type de programme *</label>
              <select
                v-model="fideliteForm.type"
                required
                class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                :class="isDark
                  ? 'border-zinc-800 bg-zinc-950 text-white focus:border-zinc-700'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
              >
                <option value="Points">Par points (selon le montant dépensé)</option>
                <option value="RDV">Par rendez-vous (nombre de visites)</option>
              </select>
            </div>

            <!-- Configuration Points -->
            <div v-if="fideliteForm.type === 'Points'" class="p-4 rounded-xl border transition-colors duration-200"
              :class="isDark ? 'border-zinc-800 bg-zinc-950/50' : 'border-gray-200 bg-gray-50'">
              <h3 class="text-sm font-medium mb-3 transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-gray-900'">Configuration Points</h3>

              <div class="space-y-3">
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                      :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Euros par point</label>
                    <input
                      v-model.number="fideliteForm.euroParPoint"
                      type="number"
                      min="0.01"
                      step="0.01"
                      required
                      class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                      :class="isDark
                        ? 'border-zinc-800 bg-zinc-900 text-white focus:border-zinc-700'
                        : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                      :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Points pour réduction</label>
                    <input
                      v-model.number="fideliteForm.pointsPourReduction"
                      type="number"
                      min="1"
                      required
                      class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                      :class="isDark
                        ? 'border-zinc-800 bg-zinc-900 text-white focus:border-zinc-700'
                        : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                      placeholder="10"
                    />
                  </div>
                </div>
                <p class="text-xs transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-gray-500'">
                  Ex: 1€ dépensé = 1 point. 10 points accumulés = réduction
                </p>
              </div>
            </div>

            <!-- Configuration RDV -->
            <div v-if="fideliteForm.type === 'RDV'" class="p-4 rounded-xl border transition-colors duration-200"
              :class="isDark ? 'border-zinc-800 bg-zinc-950/50' : 'border-gray-200 bg-gray-50'">
              <h3 class="text-sm font-medium mb-3 transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-gray-900'">Configuration Rendez-vous</h3>

              <div>
                <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Nombre de RDV pour réduction</label>
                <input
                  v-model.number="fideliteForm.rdvPourReduction"
                  type="number"
                  min="1"
                  required
                  class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                  :class="isDark
                    ? 'border-zinc-800 bg-zinc-900 text-white focus:border-zinc-700'
                    : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                  placeholder="5"
                />
                <p class="text-xs mt-2 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-gray-500'">
                  Ex: Après 5 rendez-vous, le client obtient une réduction
                </p>
              </div>
            </div>

            <!-- Récompense -->
            <div class="p-4 rounded-xl border transition-colors duration-200"
              :class="isDark ? 'border-zinc-800 bg-zinc-950/50' : 'border-gray-200 bg-gray-50'">
              <h3 class="text-sm font-medium mb-3 transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-gray-900'">Récompense</h3>

              <div class="space-y-3">
                <div>
                  <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                    :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Type de récompense</label>
                  <select
                    v-model="fideliteForm.recompenseType"
                    required
                    class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                    :class="isDark
                      ? 'border-zinc-800 bg-zinc-900 text-white focus:border-zinc-700'
                      : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                  >
                    <option value="reduction">Réduction en pourcentage</option>
                    <option value="service">Service gratuit</option>
                  </select>
                </div>

                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                      :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Pourcentage de réduction (%)</label>
                    <input
                      v-model.number="fideliteForm.reductionPourcentage"
                      type="number"
                      min="1"
                      max="100"
                      required
                      class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                      :class="isDark
                        ? 'border-zinc-800 bg-zinc-900 text-white focus:border-zinc-700'
                        : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                      placeholder="20"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium mb-2 transition-colors duration-200"
                      :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Plafond réduction (€)</label>
                    <input
                      v-model.number="fideliteForm.plafondReduction"
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-sm transition-all"
                      :class="isDark
                        ? 'border-zinc-800 bg-zinc-900 text-white focus:border-zinc-700'
                        : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'"
                      placeholder="10"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Statut -->
            <label class="flex items-center gap-3 cursor-pointer">
              <input
                v-model="fideliteForm.actif"
                type="checkbox"
                class="h-4 w-4 rounded cursor-pointer transition-colors duration-200"
                :class="isDark
                  ? 'border-zinc-800 bg-zinc-900 accent-zinc-200'
                  : 'border-gray-300 bg-white accent-amber-600'"
              />
              <span class="text-xs transition-colors duration-200"
                :class="isDark ? 'text-zinc-400' : 'text-gray-600'">
                Activer ce programme (désactivera les autres programmes actifs)
              </span>
            </label>

            <!-- Actions -->
            <div class="flex gap-3 pt-4">
              <button
                type="button"
                @click="closeFideliteModal"
                class="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
                :class="isDark
                  ? 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'"
              >
                Annuler
              </button>
              <button
                type="submit"
                :disabled="isSaving"
                class="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
                :class="isDark
                  ? 'bg-white hover:bg-[#D5CCBC] text-black'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'"
              >
                {{ isSaving ? 'Enregistrement...' : (editingProgramme !== null ? 'Modifier' : 'Créer') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

  </section>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { useTheme } from '@/composables/useTheme'
import api from '@/api'

const { isDark } = useTheme()

const activeTab = ref('promotions')
const promotions = ref([])
const programmesFidelite = ref([])
const isSaving = ref(false)

// Modal promotions
const showPromotionModal = ref(false)
const editingPromotion = ref(null)
const promotionForm = reactive({
  nom: '',
  description: '',
  type: 'percentage',
  valeur: 0,
  code: '',
  conditions: {
    montantMinimum: 0,
    premiereVisiteUniquement: false
  },
  dateDebut: '',
  dateFin: '',
  limiteUtilisationTotale: null,
  actif: true
})

// Modal fidélité
const showFideliteModal = ref(false)
const editingProgramme = ref(null)
const fideliteForm = reactive({
  nom: '',
  type: 'Points',
  euroParPoint: 1,
  pointsPourReduction: 10,
  rdvPourReduction: 5,
  recompenseType: 'reduction',
  reductionPourcentage: 20,
  plafondReduction: 10,
  actif: false
})

onMounted(async () => {
  await loadPromotions()
  await loadProgrammesFidelite()
})

// ===== PROMOTIONS =====

const loadPromotions = async () => {
  try {
    const response = await api.promotions.getAllPromotions()
    promotions.value = response.data.data || []
  } catch (error) {
    console.error('Erreur lors du chargement des promotions:', error)
    toast.error('Erreur lors du chargement des promotions')
  }
}

const openPromotionModal = (promo = null) => {
  if (promo) {
    editingPromotion.value = promo._id
    Object.assign(promotionForm, {
      nom: promo.nom,
      description: promo.description || '',
      type: promo.type,
      valeur: promo.valeur,
      code: promo.code || '',
      conditions: {
        montantMinimum: promo.conditions?.montantMinimum || 0,
        premiereVisiteUniquement: promo.conditions?.premiereVisiteUniquement || false
      },
      dateDebut: promo.dateDebut ? promo.dateDebut.substring(0, 10) : '',
      dateFin: promo.dateFin ? promo.dateFin.substring(0, 10) : '',
      limiteUtilisationTotale: promo.limiteUtilisationTotale,
      actif: promo.actif
    })
  } else {
    editingPromotion.value = null
    resetPromotionForm()
  }
  showPromotionModal.value = true
}

const closePromotionModal = () => {
  showPromotionModal.value = false
  resetPromotionForm()
}

const resetPromotionForm = () => {
  Object.assign(promotionForm, {
    nom: '',
    description: '',
    type: 'percentage',
    valeur: 0,
    code: '',
    conditions: {
      montantMinimum: 0,
      premiereVisiteUniquement: false
    },
    dateDebut: '',
    dateFin: '',
    limiteUtilisationTotale: null,
    actif: true
  })
}

const savePromotion = async () => {
  try {
    isSaving.value = true

    if (editingPromotion.value) {
      await api.promotions.updatePromotion(editingPromotion.value, promotionForm)
      toast.success('Promotion modifiée avec succès')
    } else {
      await api.promotions.createPromotion(promotionForm)
      toast.success('Promotion créée avec succès')
    }

    await loadPromotions()
    closePromotionModal()
  } catch (error) {
    console.error('Erreur lors de la sauvegarde:', error)
    toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde')
  } finally {
    isSaving.value = false
  }
}

const togglePromotion = async (id) => {
  try {
    await api.promotions.togglePromotion(id)
    toast.success('Statut de la promotion modifié')
    await loadPromotions()
  } catch (error) {
    console.error('Erreur lors du changement de statut:', error)
    toast.error('Erreur lors du changement de statut')
  }
}

const deletePromotion = async (id) => {
  if (!confirm('Êtes-vous sûr de vouloir supprimer cette promotion ?')) return

  try {
    await api.promotions.deletePromotion(id)
    toast.success('Promotion supprimée')
    await loadPromotions()
  } catch (error) {
    console.error('Erreur lors de la suppression:', error)
    toast.error('Erreur lors de la suppression')
  }
}

const formatPromoValue = (promo) => {
  if (promo.type === 'percentage') {
    return `${promo.valeur}%`
  } else if (promo.type === 'fixed_amount') {
    return `${promo.valeur}€`
  } else {
    return 'Service gratuit'
  }
}

// ===== FIDÉLITÉ =====

const loadProgrammesFidelite = async () => {
  try {
    const response = await api.fidelite.getAllProgrammes()
    programmesFidelite.value = response.data.data || []
  } catch (error) {
    console.error('Erreur lors du chargement des programmes:', error)
    toast.error('Erreur lors du chargement des programmes')
  }
}

const openFideliteModal = (programme = null, index = null) => {
  if (programme) {
    editingProgramme.value = index
    Object.assign(fideliteForm, {
      nom: programme.nom,
      type: programme.type,
      euroParPoint: programme.euroParPoint || 1,
      pointsPourReduction: programme.pointsPourReduction || 10,
      rdvPourReduction: programme.rdvPourReduction || 5,
      recompenseType: programme.recompenseType,
      reductionPourcentage: programme.reductionPourcentage || 20,
      plafondReduction: programme.plafondReduction || 10,
      actif: programme.actif || false
    })
  } else {
    editingProgramme.value = null
    resetFideliteForm()
  }
  showFideliteModal.value = true
}

const closeFideliteModal = () => {
  showFideliteModal.value = false
  resetFideliteForm()
}

const resetFideliteForm = () => {
  Object.assign(fideliteForm, {
    nom: '',
    type: 'Points',
    euroParPoint: 1,
    pointsPourReduction: 10,
    rdvPourReduction: 5,
    recompenseType: 'reduction',
    reductionPourcentage: 20,
    plafondReduction: 10,
    actif: false
  })
}

const saveProgramme = async () => {
  try {
    isSaving.value = true

    if (editingProgramme.value !== null) {
      await api.fidelite.updateProgramme(editingProgramme.value, fideliteForm)
      toast.success('Programme modifié avec succès')
    } else {
      await api.fidelite.createProgramme(fideliteForm)
      toast.success('Programme créé avec succès')
    }

    await loadProgrammesFidelite()
    closeFideliteModal()
  } catch (error) {
    console.error('Erreur lors de la sauvegarde:', error)
    toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde')
  } finally {
    isSaving.value = false
  }
}

const toggleProgramme = async (index) => {
  try {
    await api.fidelite.toggleProgramme(index)
    toast.success('Statut du programme modifié')
    await loadProgrammesFidelite()
  } catch (error) {
    console.error('Erreur lors du changement de statut:', error)
    toast.error('Erreur lors du changement de statut')
  }
}

const deleteProgramme = async (index) => {
  if (!confirm('Êtes-vous sûr de vouloir supprimer ce programme ?')) return

  try {
    await api.fidelite.deleteProgramme(index)
    toast.success('Programme supprimé')
    await loadProgrammesFidelite()
  } catch (error) {
    console.error('Erreur lors de la suppression:', error)
    toast.error('Erreur lors de la suppression')
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}
</script>

<style scoped>
/* Scrollbar personnalisée */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #52525b;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #71717a;
}
</style>
